import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import InsuranceStepIndicator, { InsuranceStep } from 'components/atoms/insurance_step_indicator';
import {
  ApiRequestStatusInfo,
  getApiRequestStatus,
  sendApiRequest,
  updateApiRequest,
} from 'services/conlicitacao_api_request';
import { ApplicationState } from 'store';
import { ProfileData, ProfileState } from 'store/modules/profile_store/types';
import { maskCnpj, validateCnpj } from 'utils/cnpj';
import { maskBrPhone } from 'utils/phone';
import { activeCompany, emptyIpRow } from './helpers';
import CompanyRequesterStep from './steps/CompanyRequesterStep';
import IpServersStep from './steps/IpServersStep';
import ReviewStep from './steps/ReviewStep';
import SalesOperationStep from './steps/SalesOperationStep';
import TechnicalLegalStep from './steps/TechnicalLegalStep';
import QuestionnaireExport from './QuestionnaireExport';
import RequestStatusScreen from './RequestStatusScreen';
import { RequestStatusKey, STATUS_BY_ID } from './statuses';
import { Container } from './styles';
import {
  ApiRequestForm,
  CompanyRequesterValues,
  IpServersValues,
  SalesOperationValues,
  TechnicalLegalValues,
} from './types';

const REQUEST_PATH = '/conlicitacao_api/solicitacao';

const REQUEST_STEPS: InsuranceStep[] = [
  { title: 'Empresa e solicitante' },
  { title: 'Responsável técnico e representante legal' },
  { title: 'Operação de vendas e sistema de destino' },
  { title: 'IPs e servidores' },
  { title: 'Revisar e enviar' },
];

const REVIEW_STEP = REQUEST_STEPS.length - 1;

const REQUEST_STEP_TITLES = REQUEST_STEPS.map((step) => step.title);

const REQUEST_STEP_LINKS = REQUEST_STEPS.map((_, index) => `${REQUEST_PATH}?etapa=${index + 1}`);

const buildInitialForm = (profile: ProfileData): ApiRequestForm => {
  const company = activeCompany(profile);

  return {
    empresa_solicitante: {
      razao_social: company?.razao_social || '',
      cnpj_contratante: maskCnpj(company?.cnpj),
      usa_outros_cnpjs: '',
      outros_cnpjs: [],
      nome_solicitante: profile.contact_name || profile.name || '',
      email_corporativo: profile.email || '',
      telefone_celular: maskBrPhone(`${profile.ddd_cellphone || ''}${profile.cellphone || ''}`),
      telefone_fixo: '',
    },
    responsavel_representante: {
      tecnico_nome: '',
      tecnico_email: '',
      tecnico_telefone: '',
      tecnico_vinculo: '',
      rep_legal_nome: '',
      rep_legal_cpf: '',
      rep_legal_tem_outras_empresas: '',
      rep_legal_outras_empresas: [],
    },
    operacao_sistema: {
      equipe_vendas: '',
      acesso_equipe: '',
      qtd_pessoas: '',
      uso_licitacoes: '',
      sistema_destino: '',
      motor_busca_nome: '',
      frequencia: '',
    },
    ips_servidores: {
      ips: [emptyIpRow(1)],
      servidores_localizacao: '',
    },
    aceite_privacidade: false,
  };
};

const stepFromSearch = (search: string) => {
  const step = Number(new URLSearchParams(search).get('etapa')) - 1;
  return Number.isInteger(step) && step > 0 ? step : 0;
};

const ConlicitacaoApiRequest: React.FC = () => {
  const history = useHistory();
  const { search } = useLocation();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [form, setForm] = useState<ApiRequestForm>(() => buildInitialForm(profile));
  const [companyLocked] = useState<boolean>(
    () =>
      Boolean(form.empresa_solicitante.razao_social) &&
      validateCnpj(form.empresa_solicitante.cnpj_contratante),
  );
  const [reachedStep, setReachedStep] = useState<number>(0);
  const [finished, setFinished] = useState<boolean>(false);
  const [returnToReview, setReturnToReview] = useState<boolean>(false);

  const [serverStatus, setServerStatus] = useState<ApiRequestStatusInfo | null>(null);
  const [statusLoaded, setStatusLoaded] = useState<boolean>(false);
  const [redoing, setRedoing] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [sendError, setSendError] = useState<string>('');

  const currentStep = Math.min(stepFromSearch(search), reachedStep);
  const previousStep = useRef<number>(currentStep);

  useEffect(() => {
    document.title = 'Solicitar acesso à API - ConLicitação';
  }, []);

  useEffect(() => {
    let active = true;

    getApiRequestStatus()
      .then((info) => {
        if (active) setServerStatus(info);
      })
      .catch(() => {
        if (active) setServerStatus(null);
      })
      .finally(() => {
        if (active) setStatusLoaded(true);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (stepFromSearch(search) !== currentStep) {
      history.replace(REQUEST_STEP_LINKS[currentStep]);
    }
  }, [search, currentStep, history]);

  useEffect(() => {
    setFinished(false);
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === REVIEW_STEP) {
      setReturnToReview(false);
    } else if (previousStep.current === REVIEW_STEP) {
      setReturnToReview(true);
    }

    previousStep.current = currentStep;
  }, [currentStep]);

  const goToStep = (step: number) => history.push(REQUEST_STEP_LINKS[step]);

  const goToNextStep = () => {
    const nextStep = Math.min(currentStep + 1, REVIEW_STEP);

    setReachedStep((prev) => Math.max(prev, nextStep));
    goToStep(nextStep);
  };

  const backToReview = returnToReview ? () => goToStep(REVIEW_STEP) : undefined;

  const sendRequest = async () => {
    setSending(true);
    setSendError('');

    try {
      // Só o ajuste (3) reaproveita o registro; recusada (5) cria outro.
      const info =
        serverStatus && serverStatus.status_id === 3
          ? await updateApiRequest(serverStatus.id, form)
          : await sendApiRequest(form);
      if (info) {
        setServerStatus(info);
        setRedoing(false);
        setFinished(true);
        window.scrollTo(0, 0);
      } else {
        setSendError('Não foi possível enviar a solicitação agora. Tente novamente em instantes.');
      }
    } catch (error) {
      const httpStatus = (error as { response?: { status?: number } })?.response?.status;

      if (httpStatus === 409) {
        const info = await getApiRequestStatus().catch(() => null);
        if (info) {
          setServerStatus(info);
          setRedoing(false);
          setFinished(true);
        } else {
          setSendError('Você já tem uma solicitação de API em andamento.');
        }
      } else {
        setSendError('Não foi possível enviar a solicitação agora. Tente novamente em instantes.');
      }
    } finally {
      setSending(false);
    }
  };

  const startRedo = () => {
    const dados = serverStatus?.dados;
    if (dados) {
      setForm((prev) => ({
        ...prev,
        ...dados,
        empresa_solicitante: { ...prev.empresa_solicitante, ...dados.empresa_solicitante },
        responsavel_representante: {
          ...prev.responsavel_representante,
          ...dados.responsavel_representante,
        },
        operacao_sistema: { ...prev.operacao_sistema, ...dados.operacao_sistema },
        ips_servidores: { ...prev.ips_servidores, ...dados.ips_servidores },
      }));
      setReachedStep(REVIEW_STEP);
    }
    setRedoing(true);
    setFinished(false);
    goToStep(0);
  };

  const updateCompanyRequester = (values: CompanyRequesterValues) =>
    setForm((prev) => ({ ...prev, empresa_solicitante: values }));

  const updateTechnicalLegal = (values: TechnicalLegalValues) =>
    setForm((prev) => ({ ...prev, responsavel_representante: values }));

  const updateSalesOperation = (values: SalesOperationValues) =>
    setForm((prev) => ({ ...prev, operacao_sistema: values }));

  const updateIpServers = (values: IpServersValues) =>
    setForm((prev) => ({ ...prev, ips_servidores: values }));

  const updateConsent = (accepted: boolean) =>
    setForm((prev) => ({ ...prev, aceite_privacidade: accepted }));

  const statusId = serverStatus ? serverStatus.status_id : undefined;

  const showStatus = !redoing && (finished || Boolean(serverStatus));
  const viewStatusKey: RequestStatusKey =
    statusId && STATUS_BY_ID[statusId] ? STATUS_BY_ID[statusId] : 'enviada';

  if (!statusLoaded && !finished) {
    return (
      <div className="page-container">
        <Container>
          <div className="container-fluid big-resolutions py-5 px-md-5">
            <div className="row justify-content-center mt-5">
              <div className="col-12 col-lg-8 mx-auto">
                <div className="card p-md-3 p-0 mb-5">
                  <div className="card-body text-center text-muted py-5">Carregando…</div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (showStatus) {
    return (
      <div className="page-container">
        <Container>
          <div className="container-fluid big-resolutions py-5 px-md-5">
            <div className="row justify-content-center mt-5">
              <div className="col-12 col-lg-8 mx-auto">
                <div className="card p-md-3 p-0 mb-5">
                  <div className="card-body">
                    <RequestStatusScreen
                      status={viewStatusKey}
                      reason={serverStatus && serverStatus.motivo ? serverStatus.motivo : null}
                      onBack={() => history.push('/conlicitacao_api')}
                      onEdit={startRedo}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Container>
        <div className="container-fluid big-resolutions py-5 px-md-5">
          <div className="row justify-content-center mt-5">
            <div className="col-12 mx-auto">
              <div className="row align-items-stretch">
                <div className="col-md-9">
                  <div className="card p-md-3 p-0 mb-5">
                    <div className="card-body">
                      <h3>Solicitar acesso à API ConLicitação</h3>
                      <h6 className="card-subtitle my-2">
                        Etapa {currentStep + 1} de {REQUEST_STEPS.length}:{' '}
                        {REQUEST_STEPS[currentStep].title}
                      </h6>

                      {currentStep < REVIEW_STEP && <QuestionnaireExport compact />}

                      {currentStep === 0 && (
                        <CompanyRequesterStep
                          values={form.empresa_solicitante}
                          companyLocked={companyLocked}
                          onChange={updateCompanyRequester}
                          onNext={goToNextStep}
                          onCancel={() => history.push('/conlicitacao_api')}
                          onReturnToReview={backToReview}
                        />
                      )}

                      {currentStep === 1 && (
                        <TechnicalLegalStep
                          values={form.responsavel_representante}
                          onChange={updateTechnicalLegal}
                          onNext={goToNextStep}
                          onBack={() => goToStep(0)}
                          onReturnToReview={backToReview}
                        />
                      )}

                      {currentStep === 2 && (
                        <SalesOperationStep
                          values={form.operacao_sistema}
                          onChange={updateSalesOperation}
                          onNext={goToNextStep}
                          onBack={() => goToStep(1)}
                          onReturnToReview={backToReview}
                        />
                      )}

                      {currentStep === 3 && (
                        <IpServersStep
                          values={form.ips_servidores}
                          onChange={updateIpServers}
                          onNext={goToNextStep}
                          onBack={() => goToStep(2)}
                        />
                      )}

                      {currentStep === REVIEW_STEP && (
                        <ReviewStep
                          form={form}
                          stepTitles={REQUEST_STEP_TITLES}
                          onConsentChange={updateConsent}
                          onSend={sendRequest}
                          onBack={() => goToStep(3)}
                          submitting={sending}
                          submitError={sendError}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-3 d-none d-md-block">
                  <InsuranceStepIndicator
                    currentStep={currentStep}
                    steps={REQUEST_STEPS}
                    links={REQUEST_STEP_LINKS}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ConlicitacaoApiRequest;
